import React, { Component } from 'react';
import { withTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';

/**
 * ErrorBoundary component that catches JavaScript errors anywhere in the component tree,
 * logs those errors, and displays a fallback UI instead of the component tree that crashed.
 * 
 * This provides better UX as suggested by React Router's error message.
 */
class ErrorBoundaryComponent extends Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error) {
        // Update state so the next render will show the fallback UI
        return { hasError: true };
    }

    componentDidCatch(error, errorInfo) {
        // Log the error to the console or an error reporting service
        console.error('ErrorBoundary caught an error:', error, errorInfo);
        
        this.setState({
            error: error,
            errorInfo: errorInfo
        });

        // You can also log the error to an error reporting service here
        Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } });
    }

    handleReload = () => {
        window.location.reload();
    }

    render() {
        const { t } = this.props;
        if (this.state.hasError) {
            // Fallback UI
            return (
                <div style={{
                    padding: '20px',
                    textAlign: 'center',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #dee2e6',
                    borderRadius: '0.375rem',
                    margin: '20px'
                }}>
                    <h2 style={{ color: '#dc3545', marginBottom: '16px' }}>
                        🚨 {t('error_boundary.something_went_wrong')}
                    </h2>
                    <p style={{ marginBottom: '16px' }}>
                        {t('error_boundary.unexpected_error_message')}
                    </p>
                    
                    {this.props.showDetails && this.state.error && (
                        <details style={{ 
                            textAlign: 'left', 
                            marginBottom: '16px',
                            padding: '10px',
                            backgroundColor: '#fff',
                            border: '1px solid #dee2e6',
                            borderRadius: '0.25rem'
                        }}>
                            <summary style={{ cursor: 'pointer', fontWeight: 'bold' }}>
                                {t('error_boundary.error_details')}
                            </summary>
                            <pre style={{ 
                                marginTop: '10px', 
                                fontSize: '12px', 
                                whiteSpace: 'pre-wrap',
                                color: '#dc3545'
                            }}>
                                {this.state.error.toString()}
                                {this.state.errorInfo.componentStack}
                            </pre>
                        </details>
                    )}
                    
                    <div>
                        <button 
                            onClick={this.handleReload}
                            style={{
                                backgroundColor: '#007bff',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '0.25rem',
                                cursor: 'pointer',
                                marginRight: '8px'
                            }}
                        >
                            {t('error_boundary.reload_page')}
                        </button>
                        
                        <button 
                            onClick={() => this.setState({ hasError: false, error: null, errorInfo: null })}
                            style={{
                                backgroundColor: '#6c757d',
                                color: 'white',
                                border: 'none',
                                padding: '8px 16px',
                                borderRadius: '0.25rem',
                                cursor: 'pointer'
                            }}
                        >
                            {t('error_boundary.try_again')}
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export const ErrorBoundary = withTranslation()(ErrorBoundaryComponent); 